import TableButton from 'components/Buttons/TableButtons/TableButton'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { FilterCriteriaSelector } from 'components/Filters/components/FilterCriteriaSelector'
import { FilterStore } from 'components/Filters/FilterStore'
import { cn } from 'components/UI/cn'
import { Modal } from 'components/UI/Modal/Modal'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { FilterIdentifier } from 'models/saved_view'
import React, { useEffect, useMemo, useState } from 'react'
import { AiOutlineSave } from 'react-icons/ai'
import { IoChevronDown, IoChevronUp } from 'react-icons/io5'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components/macro'
import { CreateSavedFilterModal } from '../saved-filters/CreateSavedFilterModal'
import SelectSavedFilter from '../saved-filters/SelectSavedFilter'
import { PiCaretDown, PiCaretUp } from 'react-icons/pi'
import { BiFilterAlt } from 'react-icons/bi'

export default function FilterModal({
  children,
  store,
  identifier,
  disableSavedFilters,
  buttonText = 'Filter',
}: {
  store: FilterStore
  identifier: FilterIdentifier
  children?: React.ReactNode
  disableSavedFilters?: boolean
  buttonText?: string
}) {
  const [openFilterSaveModal, setOpenFilterSaveModal] = React.useState(false)
  const [openFilterModal, setOpenFilterModal] = React.useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    applyFilters,
    setPendingCriteria,
    pendingCriteria,
    valueFilters,
    rangeFilters,
    zipCodeFilter,
    search,
    syncPendingFilters,
  } = store()

  const filterCount = useMemo(() => {
    const valueCount = Object.keys(valueFilters).length
    const rangeCount = Object.keys(rangeFilters).length
    const searchCount = Object.keys(search).length
    const zipCount = zipCodeFilter[0] ? 1 : 0

    return valueCount + rangeCount + searchCount + zipCount
  }, [valueFilters, rangeFilters, search, zipCodeFilter])

  useEffect(() => syncPendingFilters(), [openFilterModal])

  const removePageFromUrlSearchParams = () => {
    const searchParamsCopy = new URLSearchParams(searchParams)
    searchParamsCopy.delete('page')
    setSearchParams(searchParamsCopy)
  }

  return (
    <>
      <TableButton
        text={buttonText}
        icon={BiFilterAlt}
        iconSize={22}
        activeValue={filterCount > 0 ? filterCount.toString() : null}
        postpendIcon={
          filterCount > 0 && !disableSavedFilters ? AiOutlineSave : undefined
        }
        onPostpendIconClick={() => setOpenFilterSaveModal(true)}
        onClick={() => setOpenFilterModal(true)}
        type="button"
      />
      <Modal
        open={openFilterModal}
        onOpenChange={(open) => setOpenFilterModal(open)}
        size="lg"
        title={'All filters'}
        onAccept={() => {
          applyFilters()
          removePageFromUrlSearchParams()
          setOpenFilterModal(false)
        }}
        acceptButtonText={'Apply'}
        footerChildren={
          <>
            <FilterModalFooterCriteriaHeader>
              Show results matching
              <FilterCriteriaSelector
                value={pendingCriteria}
                onChange={setPendingCriteria}
              />
              of the following criteria:
            </FilterModalFooterCriteriaHeader>
            <FilterModalFooterCriteria>
              <FilterChips
                identifier={identifier}
                store={store}
                className="ml-0"
              />
            </FilterModalFooterCriteria>
          </>
        }
      >
        {!disableSavedFilters && (
          <div className="mb-2">
            <SelectSavedFilter filterStore={store} identifier={identifier} />
          </div>
        )}
        {children}
      </Modal>
      {!disableSavedFilters && (
        <CreateSavedFilterModal
          show={openFilterSaveModal}
          handleClose={() => setOpenFilterSaveModal(false)}
          identifier={identifier}
          filterStore={store}
        />
      )}
    </>
  )
}

export function QuickFilterGroup({ children }: { children: React.ReactNode }) {
  return (
    <>
      <FilterGroup title="Quick filters" fontStyle="italic">
        {children}
      </FilterGroup>
      <Divider />
    </>
  )
}

export function MobileFilterGroup({
  children,
  fontStyle,
  title,
  className,
}: {
  children: React.ReactNode
  fontStyle?: string
  title: string
  className?: string
}) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className={'mt-3 mb-2'}>
      <div
        className={'text-md font-medium mb-2 flex justify-between'}
        style={{ fontStyle }}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{title}</span>
        {isOpen ? <PiCaretUp /> : <PiCaretDown />}
      </div>
      {isOpen && (
        <div className={cn('flex flex-col gap-2', className)}>{children}</div>
      )}
    </div>
  )
}

export function FilterGroup({
  children,
  fontStyle,
  title,
  className,
  defaultCollapsed,
}: {
  children: React.ReactNode
  fontStyle?: string
  title: string
  className?: string
  defaultCollapsed?: boolean
}) {
  // Default: collapsed on mobile (< 1024px), expanded on desktop
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed ?? false)

  // Sync with mobile state on mount/resize
  useEffect(() => {
    if (defaultCollapsed === undefined) {
      setIsCollapsed(isMobile)
    }
  }, [isMobile, defaultCollapsed])

  return (
    <div className="py-[16px] border-t border-line first:border-t-0 first:pt-0">
      <div
        className="flex items-center justify-between cursor-pointer pr-3"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <span className="text-md font-medium leading-5" style={{ fontStyle }}>
          {title}
        </span>
        {isCollapsed ? (
          <IoChevronDown className="w-4 h-4 text-fg-muted" />
        ) : (
          <IoChevronUp className="w-4 h-4 text-fg-muted" />
        )}
      </div>
      <div
        className={cn(
          'grid transition-all duration-200 ease-in-out overflow-hidden',
          isCollapsed ? 'grid-rows-[0fr] mt-0' : 'grid-rows-[1fr] mt-[16px]'
        )}
      >
        <div
          className={cn(
            'min-h-0 grid grid-cols-2 lg:grid-cols-3 gap-2',
            className
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

export const Divider = styled.hr`
  border: 1px solid #e5e5e5;
  margin: 16px 0;
`

export const FilterModalFooterCriteriaHeader = styled.div`
  padding: 16px 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

export const FilterModalFooterCriteria = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;

  & > span {
    margin: 4px;
  }
`
